import { Modal, Button, Surface, Dropdown } from "@heroui/react";
import { Person, ChevronDown } from "@gravity-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import { clearLogin } from "../../reducers/loginReducer";
import apiSlice from "../../reducers/apiSlice";
import { LoginFormFlow, type LoginFormFlowClassNames } from "../login";
import formSkin from "../login/loginFormModalSkin.module.scss";
import styles from "./loginModal.module.scss";
import { useLoginModalController } from "./loginModalContext";

/* UI-DESIGN: LB-61 the header renders LoginModal twice (desktop nav and,
   since LB-60b, the phone bar) over one shared open state; only one copy
   may own the dialog, or Log in opens two stacked dialogs. */
const LoginModal = ({ dialog = true }: { dialog?: boolean }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.login.user);
  const { isOpen, setOpen, openLoginModal, onLoginFlowComplete } = useLoginModalController();

  const handleLogOut = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    dispatch(clearLogin());
    dispatch(apiSlice.util.resetApiState());
  };

  return (
    <Modal>
      {user ? (
        <Dropdown>
          {/* UI-DESIGN: incomplete profiles need a named account control, not an empty avatar. */}
          <Dropdown.Trigger className={styles.avatarMenuTrigger} aria-label="Account menu">
            <span>Account</span>
            <ChevronDown width={14} height={14} aria-hidden />
          </Dropdown.Trigger>
          <Dropdown.Popover placement="bottom end" className={styles.accountPopover}>
            <Dropdown.Menu aria-label="Account" className={styles.accountMenu}>
              <Dropdown.Item id="logout" onAction={handleLogOut} className={styles.accountItem}>
                Log out
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      ) : (
        /* UI-DESIGN: LB-61 no Modal.Trigger wrapper: it is a focusable
           role="button" div around this button, so keyboard users met
           Log in twice. The modal opens from the controlled state below. */
        <Button
          variant="ghost"
          className={styles.trigger}
          aria-label="Log in"
          onPress={() => openLoginModal()}
        >
          {/* UI-DESIGN: "Log in" per the lightbox nav; the modal still covers sign-up.
              LB-60b (Avi) a person mark so returning players spot it at once */}
          <Person width={16} height={16} aria-hidden />
          <span className={styles.triggerLabel}>Log in</span>
        </Button>
      )}
      {dialog ? (
      <Modal.Backdrop variant="blur" isOpen={isOpen} onOpenChange={setOpen}>
        <Modal.Container placement="center" className={styles.loginModal}>
          <Modal.Dialog aria-labelledby="login-title" className={`modal__dialog ${styles.dialog}`}>
            {/* UI-DESIGN: name the dialog and provide a thumb-sized escape from the flow. */}
            <div className={styles.headingRow}>
              <Modal.Heading id="login-title" className={styles.title}>Log in</Modal.Heading>
              <Button variant="ghost" aria-label="Close login" className={styles.close} onPress={() => setOpen(false)}>×</Button>
            </div>
            <Modal.Body className={`modal__body ${styles.body}`}>
              <Surface variant="default" className={styles.surface}>
                <LoginFormFlow
                  classNames={formSkin as LoginFormFlowClassNames}
                  uponVerificationComplete={onLoginFlowComplete}
                />
              </Surface>
            </Modal.Body>
            <Modal.Footer className="hidden" />
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
      ) : null}
    </Modal>
  );
};

export default LoginModal;
